import React, { useState } from 'react';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Package,
  Scan,
  Eye,
  User,
  Clock,
  MapPin,
  Truck,
  AlertCircle,
  Copy,
  ArrowUpDown,
  Save,
  FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';

// Mock data cho danh sách xe và tài xế
const mockVehicles = [
  { id: '1', licensePlate: '29A-12345', driver: 'Trần Văn B', status: 'Sẵn sàng' },
  { id: '2', licensePlate: '51B-67890', driver: 'Phạm Văn D', status: 'Sẵn sàng' },
  { id: '3', licensePlate: '30G-11111', driver: 'Lê Văn F', status: 'Đang hoạt động' },
  { id: '4', licensePlate: '43C-22222', driver: 'Nguyễn Văn G', status: 'Sẵn sàng' },
  { id: '5', licensePlate: '92H-33333', driver: 'Hoàng Thị H', status: 'Bảo trì' },
  { id: '6', licensePlate: '14K-44444', driver: 'Lê Văn I', status: 'Sẵn sàng' }
];

// Mock data for transit bags
const mockTransitBags = [
  {
    id: 'BAG001',
    seatCode: 'ST001',
    createdBy: 'Nguyễn Văn A',
    createdAt: '2025-01-12T14:30:00+07:00',
    receivingHub: 'Bưu cục Đà Nẵng',
    driver: 'Trần Văn B',
    licensePlate: '29A-12345',
    status: 'Chờ luân chuyển',
    orders: ['DH001', 'DH002', 'DH003'],
    totalOrders: 3,
    image: null,
    notes: 'Hàng thường'
  },
  {
    id: 'BAG002',
    seatCode: 'ST002',
    createdBy: 'Lê Thị C',
    createdAt: '2025-01-12T10:15:00+07:00',
    receivingHub: 'Bưu cục Cần Thơ',
    driver: 'Phạm Văn D',
    licensePlate: '51B-67890',
    status: 'Đang luân chuyển',
    orders: ['DH004', 'DH005'],
    totalOrders: 2,
    image: null,
    notes: 'Hàng dễ vỡ'
  },
  {
    id: 'BAG003',
    seatCode: 'ST003',
    createdBy: 'Hoàng Văn E',
    createdAt: '2025-01-12T08:20:00+07:00',
    receivingHub: 'Bưu cục Hải Phòng',
    driver: 'Lê Văn F',
    licensePlate: '30G-11111',
    status: 'Nháp',
    orders: ['DH006', 'DH007', 'DH008'],
    totalOrders: 3,
    image: null,
    notes: 'Bao hàng khẩn cấp'
  }
];

const TransitBags = () => {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedBag, setSelectedBag] = useState(null);
  const [step, setStep] = useState(1);
  const [orderInput, setOrderInput] = useState('');
  const [ordersList, setOrdersList] = useState([]);
  const [hoveredField, setHoveredField] = useState(null);
  const [formData, setFormData] = useState({
    seatCode: '',
    receivingHub: '',
    driver: '',
    licensePlate: '',
    notes: ''
  });

  // Copy function
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // Using simple alert for TransitBags to avoid toast dependency
      alert("Đã sao chép vào clipboard!");
    } catch (err) {
      console.error('Failed to copy: ', err);
      alert("Không thể sao chép");
    }
  };

  const [searchTerm, setSearchTerm] = useState('');
  const filteredBags = mockTransitBags.filter(bag =>
    bag.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    bag.seatCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
    bag.receivingHub.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'Nháp':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'Mới tạo':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      case 'Chờ luân chuyển':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'Đang luân chuyển':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'Đã nhập kho':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const handleAddOrder = () => {
    if (orderInput.trim() && !ordersList.includes(orderInput.trim())) {
      setOrdersList([...ordersList, orderInput.trim()]);
      setOrderInput('');
    }
  };

  const handleRemoveOrder = (order) => {
    setOrdersList(ordersList.filter(o => o !== order));
  };

  const handleConfirmOrders = () => {
    if (ordersList.length > 0) {
      setStep(2);
    }
  };

  // Xử lý tạo bao chính thức
  const handleCreateBag = () => {
    console.log('Creating bag with data:', formData, 'Orders:', ordersList);
    // Simulate bag creation with random ID
    const newBagId = `BAG${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`;
    alert(`Bao trung chuyển ${newBagId} đã được tạo thành công! Thông báo đã gửi tới tài xế ${formData.driver}.`);
    resetCreateDialog();
  };

  // Xử lý tạo bao nháp
  const handleCreateDraftBag = () => {
    console.log('Creating draft bag with data:', formData, 'Orders:', ordersList);
    // Simulate draft bag creation with random ID
    const newBagId = `BAG${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`;
    alert(`Bao nháp ${newBagId} đã được lưu thành công! Bạn có thể chỉnh sửa và hoàn thiện sau.`);
    resetCreateDialog();
  };

  // Reset dialog tạo bao
  const resetCreateDialog = () => {
    setShowCreateDialog(false);
    setStep(1);
    setOrdersList([]);
    setFormData({
      seatCode: '',
      receivingHub: '',
      driver: '',
      licensePlate: '',
      notes: ''
    });
  };

  // Xử lý chỉnh sửa bao
  const handleEditBag = (bag) => {
    setSelectedBag(bag);
    setFormData({
      seatCode: bag.seatCode,
      receivingHub: bag.receivingHub,
      driver: bag.driver,
      licensePlate: bag.licensePlate,
      notes: bag.notes
    });
    setOrdersList([...bag.orders]);
    setShowEditDialog(true);
  };

  // Xử lý cập nhật bao
  const handleUpdateBag = () => {
    console.log('Updating bag:', selectedBag.id, 'with data:', formData, 'Orders:', ordersList);
    alert(`Bao ${selectedBag.id} đã được cập nhật thành công!`);
    setShowEditDialog(false);
    setSelectedBag(null);
    setOrdersList([]);
    setFormData({
      seatCode: '',
      receivingHub: '',
      driver: '',
      licensePlate: '',
      notes: ''
    });
  };

  // Xử lý chọn xe (tự động điền tài xế)
  const handleVehicleSelect = (licensePlate) => {
    const vehicle = mockVehicles.find(v => v.licensePlate === licensePlate);
    if (vehicle) {
      setFormData({
        ...formData,
        licensePlate: vehicle.licensePlate,
        driver: vehicle.driver
      });
    }
  };

  const handleScanQR = () => {
    // Simulate QR scan with camera
    const mockOrderId = `DH${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`;
    if (!ordersList.includes(mockOrderId)) {
      setOrdersList([...ordersList, mockOrderId]);
      alert(`Đã quét thành công đơn hàng: ${mockOrderId}`);
    }
  };

  const handleDeleteBag = (bag) => {
    if (confirm(`Bạn có chắc muốn xóa bao ${bag.id}? Tất cả đơn hàng (${bag.orders.join(', ')}) sẽ chuyển về trạng thái trước đó.`)) {
      console.log(`Deleting bag ${bag.id}`);
      alert(`Bao ${bag.id} đã được xóa. Các đơn hàng đã chuyển về trạng thái trước đó.`);
    }
  };

  // Render form tạo/sửa bao
  const renderBagForm = (isEdit = false) => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Mã SEAT</Label>
          <Input 
            value={formData.seatCode}
            onChange={(e) => setFormData({...formData, seatCode: e.target.value})}
            placeholder="Nhập mã SEAT" 
          />
        </div>
        <div className="space-y-2">
          <Label>Bưu cục nhận</Label>
          <Select 
            value={formData.receivingHub}
            onValueChange={(value) => setFormData({...formData, receivingHub: value})}
          >
            <SelectTrigger>
              <SelectValue placeholder="Chọn bưu cục" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Bưu cục Đà Nẵng">Bưu cục Đà Nẵng</SelectItem>
              <SelectItem value="Bưu cục Cần Thơ">Bưu cục Cần Thơ</SelectItem>
              <SelectItem value="Bưu cục Hải Phòng">Bưu cục Hải Phòng</SelectItem>
              <SelectItem value="Bưu cục TP.HCM">Bưu cục TP.HCM</SelectItem>
              <SelectItem value="Bưu cục Hà Nội">Bưu cục Hà Nội</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Biển số xe</Label>
          <Select 
            value={formData.licensePlate}
            onValueChange={handleVehicleSelect}
          >
            <SelectTrigger>
              <SelectValue placeholder="Chọn xe" />
            </SelectTrigger>
            <SelectContent>
              {mockVehicles.map((vehicle) => (
                <SelectItem 
                  key={vehicle.id} 
                  value={vehicle.licensePlate}
                  disabled={vehicle.status !== 'Sẵn sàng'}
                >
                  {vehicle.licensePlate} - {vehicle.status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Tài xế</Label>
          <Input 
            value={formData.driver}
            readOnly
            placeholder="Tự động điền khi chọn xe"
            className="bg-muted"
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label>Ghi chú</Label>
        <Textarea 
          value={formData.notes}
          onChange={(e) => setFormData({...formData, notes: e.target.value})}
          placeholder="Nhập ghi chú (tùy chọn)" 
        />
      </div>
      
      <div className="border rounded-lg p-4">
        <h4 className="font-medium mb-2">Đơn hàng trong bao ({ordersList.length})</h4>
        {ordersList.length > 0 ? (
          <div className="grid grid-cols-4 gap-2 text-sm">
            {ordersList.map((order, index) => (
              <div key={index} className="flex items-center justify-between bg-muted px-2 py-1 rounded">
                <span className="font-mono">{order}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-4 w-4 p-0"
                  onClick={() => handleRemoveOrder(order)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Chưa có đơn hàng nào</p>
        )}
      </div>
      
      <div className="flex justify-end space-x-2">
        <Button 
          type="button" 
          variant="outline" 
          onClick={() => {
            if (isEdit) {
              setShowEditDialog(false);
            } else {
              setStep(1);
            }
          }}
        >
          {isEdit ? 'Hủy' : 'Quay lại'}
        </Button>
        {!isEdit && (
          <Button 
            variant="outline"
            onClick={handleCreateDraftBag}
            disabled={ordersList.length === 0}
          >
            <FileText className="h-4 w-4 mr-2" />
            Lưu nháp
          </Button>
        )}
        <Button 
          onClick={isEdit ? handleUpdateBag : handleCreateBag}
          disabled={ordersList.length === 0 || !formData.licensePlate}
        >
          <Save className="h-4 w-4 mr-2" />
          {isEdit ? 'Cập nhật' : 'Tạo bao'}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-foreground">Bao trung chuyển</h1>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90">
              <Plus className="h-4 w-4 mr-2" />
              Tạo bao trung chuyển
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {step === 1 ? 'Thêm đơn hàng vào bao' : 'Thông tin bao trung chuyển'}
              </DialogTitle>
            </DialogHeader>
            
            {step === 1 && (
              <div className="space-y-4">
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Nhập mã đơn hàng hoặc quét QR code để thêm vào bao trung chuyển
                  </AlertDescription>
                </Alert>
                
                <div className="flex space-x-2">
                  <Input
                    placeholder="Nhập mã đơn hàng (VD: DH001)"
                    value={orderInput}
                    onChange={(e) => setOrderInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddOrder()}
                    className="flex-1"
                  />
                  <Button onClick={handleAddOrder} variant="outline">
                    Thêm
                  </Button>
                  <Button onClick={handleScanQR} variant="outline">
                    <Scan className="h-4 w-4 mr-2" />
                    Quét mã
                  </Button>
                </div>
                
                {ordersList.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium">Danh sách đơn hàng ({ordersList.length})</h4>
                    <div className="border rounded-lg p-4 max-h-64 overflow-y-auto">
                      {ordersList.map((order, index) => (
                        <div key={index} className="flex items-center justify-between py-2 border-b last:border-b-0">
                          <span className="font-mono">{order}</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRemoveOrder(order)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={resetCreateDialog}>
                    Hủy
                  </Button>
                  <Button 
                    onClick={handleConfirmOrders}
                    disabled={ordersList.length === 0}
                  >
                    Xác nhận ({ordersList.length} đơn)
                  </Button>
                </div>
              </div>
            )}

            {step === 2 && renderBagForm(false)}
          </DialogContent>
        </Dialog>
      </div>

      {/* Dialog chỉnh sửa bao */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Chỉnh sửa bao trung chuyển - {selectedBag?.id}</DialogTitle>
          </DialogHeader>
          {renderBagForm(true)}
        </DialogContent>
      </Dialog>

      <Card className="animate-fade-in">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <Package className="h-5 w-5 mr-2 text-primary" />
              Danh sách bao trung chuyển
            </CardTitle>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
              <Input
                placeholder="Tìm kiếm bao..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredBags.map((bag) => (
              <div key={bag.id} className="p-4 border rounded-lg hover:shadow-md transition-all duration-200">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="bg-primary/10 p-2 rounded-lg">
                      <Package className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <h3 
                        className="font-semibold text-lg cursor-pointer flex items-center gap-2"
                        onMouseEnter={() => setHoveredField(`id-${bag.id}`)}
                        onMouseLeave={() => setHoveredField(null)}
                        onClick={() => copyToClipboard(bag.id)}
                      >
                        {bag.id}
                        {hoveredField === `id-${bag.id}` && (
                          <Copy className="h-3 w-3 text-muted-foreground" />
                        )}
                      </h3>
                      <p 
                        className="text-sm text-muted-foreground cursor-pointer flex items-center gap-1"
                        onMouseEnter={() => setHoveredField(`seat-${bag.id}`)}
                        onMouseLeave={() => setHoveredField(null)}
                        onClick={() => copyToClipboard(bag.seatCode)}
                      >
                        SEAT: {bag.seatCode}
                        {hoveredField === `seat-${bag.id}` && (
                          <Copy className="h-3 w-3 text-muted-foreground" />
                        )}
                      </p>
                    </div>
                  </div>
                  <Badge className={getStatusColor(bag.status)}>
                    {bag.status}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <User className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span>Tạo bởi: {bag.createdBy}</span>
                    </div>
                    <div className="flex items-center">
                      <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span>{bag.receivingHub}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <Truck className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span>{bag.driver} - {bag.licensePlate}</span>
                    </div>
                    <div className="flex items-center">
                      <Package className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span className="font-medium text-primary">
                        {bag.totalOrders} đơn hàng
                      </span>
                    </div>
                  </div>
                </div>

                {bag.notes && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-sm text-muted-foreground">{bag.notes}</p>
                  </div>
                )}

                <div className="flex items-center justify-between mt-3 pt-3 border-t">
                  <span className="text-sm text-muted-foreground">
                    Tạo: {new Date(bag.createdAt).toLocaleString('vi-VN')}
                  </span>
                  <div className="flex space-x-2">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="outline">
                          <Eye className="h-4 w-4 mr-1" />
                          Xem đơn
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Danh sách đơn hàng - {bag.id}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-2">
                          {bag.orders.map((order, index) => (
                            <div key={index} className="p-3 border rounded-lg">
                              <span className="font-mono">{order}</span>
                            </div>
                          ))}
                        </div>
                      </DialogContent>
                    </Dialog>
                    
                    {/* Chỉ cho phép sửa khi không đang luân chuyển */}
                    {bag.status !== 'Đang luân chuyển' && (
                      <>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleEditBag(bag)}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Sửa
                        </Button>
                        
                        <Button 
                          size="sm" 
                          variant="destructive" 
                          onClick={() => handleDeleteBag(bag)}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Xóa
                        </Button>
                      </>
                    )}
                    {bag.status === 'Đang luân chuyển' && (
                      <Badge variant="outline" className="text-blue-600">
                        Đã yêu cầu
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TransitBags;